import React from 'react';
import _ from 'lodash';
import { Variable } from 'src/api/types';
import { Poppins } from 'src/common';
import { ClickableVariablePill, noDataTooltipHtml } from 'src/common/VariablePill';
import { useTheme } from 'styled-components';
import { formatNumber } from 'src/utils/misc';
import { findVariableByToken } from 'src/components/FormulaInput/shared/formula';
import { getVariableIcon } from 'src/components/FormulaInput/comps/variableIcon';

interface VariableCapsuleProps {
  id: string;
  variables: Variable[];
  /** Names of workspace/global variables — drives override-icon detection. */
  workspaceVariableNames: Set<string>;
  /** When set (+ tooltipId), reveal the resolved value on hover. */
  showValueOnHover?: boolean;
  tooltipId?: string;
  /**
   * When supplied AND the token resolves to a real variable, the pill becomes a
   * clickable button that reports the resolved variable id (e.g. to open the
   * Variables modal at that row). Unresolved `#id` tokens stay non-clickable.
   */
  onVariableClick?: (variableId: number) => void;
}

// Read-only formula variable chip. Resolution (incl. assessment→workspace
// override) and icon selection are reused VERBATIM from the edit form
// (findVariableByToken + getVariableIcon) so view/library/PDF match editing.
// The pill chrome + the red "unpopulated" treatment (unresolved token, or resolved
// but no value → red info-circle chip) live in the shared VariablePill.
export const VariableCapsule: React.FC<VariableCapsuleProps> = ({
  id,
  variables,
  workspaceVariableNames,
  showValueOnHover,
  tooltipId,
  onVariableClick,
}) => {
  const colors = useTheme().colors;
  const variable = findVariableByToken(variables, id);

  const hasValue = !!variable && !_.isNil(variable.value);
  const unpopulated = !hasValue;
  // Keep an unresolved (deleted/absent) token legible as #id rather than dropping it.
  const label = variable ? variable.alias || variable.name || `#${variable.id}` : `#${id}`;

  // Only a resolved variable has a row to jump to, so only then is the pill actionable.
  const clickable = !!variable && !!onVariableClick;

  const hoverProps =
    showValueOnHover && tooltipId
      ? {
          'data-tooltip-id': tooltipId,
          'data-tooltip-html': hasValue
            ? formatNumber(variable!.value || 0, { symbol: !!variable!.currency, maxDecimals: 3 })
            : noDataTooltipHtml(colors.statusRed),
        }
      : {};

  return (
    <ClickableVariablePill
      clickable={clickable}
      onPillClick={clickable ? () => onVariableClick!(variable!.id) : undefined}
      unpopulated={unpopulated}
      icon={unpopulated ? undefined : getVariableIcon(variable!, workspaceVariableNames)}
      {...hoverProps}
    >
      <Poppins px={14} weight={500}>
        {label}
      </Poppins>
    </ClickableVariablePill>
  );
};
